import * as borogove from "borogove";
import { CircularProgress, Button, TextField, Stack } from "@mui/material";
import { useQueryState } from "nuqs";
import {
	Await,
	useNavigate,
	useOutletContext,
	useAsyncValue,
	useAsyncError,
} from "react-router";
import { Suspense, useState, FormEvent } from "react";

import { Context, Accounts } from "../App";
import { newAccount } from "./Login";
import { href } from "../util";

const inputType = (field: borogove.FormField) => {
	if (field.type == "text-private") return "password";
	if (field.datatype == "boolean") return "checkbox";
	if (field.datatype.startsWith("html:")) return field.datatype.slice(5);
	if (field.datatype == "xs:anyURI") return "url";
	if (field.datatype == "xs:dateTime") return "datetime-local";
	if (field.datatype == "xs:data") return "date";
	if (field.datatype == "xs:time") return "time";
	if (
		[
			"xs:byte",
			"xs:decimal",
			"xs:double",
			"xs:int",
			"xs:integer",
			"xs:long",
			"xs:short",
		].includes(field.datatype)
	) {
		if (field.rangeMin && field.rangeMax) return "range";
		return "number";
	}
	return "text";
};

const FormField = ({ field }: { field: borogove.FormField }) => {
	// TODO: options as datalist or select
	const step = ["xs:decimal", "xs:double"].includes(field.datatype)
		? "any"
		: undefined;
	return (
		<TextField
			label={field.label}
			name={field.name}
			defaultValue={field.value.join("\n")}
			fullWidth
			multiline={field.type == "text-multiline"}
			required={field.required}
			type={inputType(field)}
			slotProps={{
				htmlInput: {
					step,
					multiple: ["list-multi", "text-multi"].includes(field.type),
					inputMode: field.datatype === "jid" ? "email" : undefined,
					pattern: field.regex ?? undefined,
					min: field.rangeMin ?? undefined,
					max: field.rangeMax ?? undefined,
				},
			}}
		/>
	);
};

const FormSection = ({ section }: { section: borogove.FormSection }) => {
	return (
		<section>
			<Stack spacing={1}>
				{section.title() && <h1>{section.title()}</h1>}
				{section.items().map((item) => (
					<>
						{item.text && <p>{item.text}</p>}
						{item.field && <FormField field={item.field} />}
						{item.section && <FormSection section={item.section} />}
					</>
				))}
			</Stack>
		</section>
	);
};

const RegisterInner = () => {
	const navigate = useNavigate();
	const { setAccounts, persistence, idbp } = useOutletContext<Context>();
	const [register, form] = useAsyncValue() as [
		borogove.Register,
		borogove.FormSection,
	];
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		const data = new FormData(e.currentTarget);
		try {
			const accountId = await register.submit(data);
			if (accountId) {
				const account = newAccount(accountId, idbp, persistence);
				if (data.has("password")) {
					const token = account.client.addPasswordNeededListener(() => {
						account.client.usePassword("" + data.get("password"));
						account.client.removeEventListener(token);
					});
				}
				setAccounts((accounts: Accounts) => ({
					...accounts,
					[accountId]: account,
				}));
				navigate(href("/accounts/:accountId", { accountId }));
			} else {
				navigate("/login");
			}
		} catch (ex) {
			console.error(ex);
			setError("" + ex);
		}
	};

	return (
		<form style={{ margin: "auto" }} method="dialog" onSubmit={handleSubmit}>
			<Stack spacing={1}>
				<FormSection section={form} />
				{error && <p>Error: {error}</p>}
				<Button type="submit" variant="contained">
					Register
				</Button>
			</Stack>
		</form>
	);
};

const RegisterError = () => {
	const error = useAsyncError();

	return (
		<p style={{ margin: "auto" }}>Error connecting to server: {"" + error}</p>
	);
};

const registerAndForm = async (domainOrInvite: string) => {
	const register = await borogove.Register.fromDomainOrInvite(domainOrInvite);
	const form = await register.getForm();
	return [register, form];
};

export const Register = () => {
	const [with_] = useQueryState("with");
	const promise =
		with_ == null
			? Promise.reject("No server or invite provided")
			: registerAndForm(with_);

	return (
		<Suspense
			key={with_}
			fallback={<CircularProgress className="fullscreen" />}
		>
			<Await resolve={promise} errorElement={<RegisterError />}>
				<RegisterInner />
			</Await>
		</Suspense>
	);
};
